/* eslint-disable @next/next/no-img-element */
import { Merge, X } from "lucide-react";
import type { MergeChip } from "./merge-eligibility";
import { formatPrice, formatSubscriptionDateForDisplay } from "./formatters";

export interface MergeSubscriptionsBannerProps {
  onMerge: () => void;
  onDismiss: () => void;
  /** Chips for the subscriptions that can be merged (name + next date). */
  chips?: MergeChip[];
  /** When true, merging saves on shipping — adds the bonus-savings line. */
  savesShipping?: boolean;
  /**
   * Concrete shipping saving (in store currency) when it can be computed from
   * real per-subscription shipping fees. When set, the bonus line shows the
   * amount; otherwise it falls back to the qualitative {@link savesShipping}
   * wording.
   */
  savingsAmount?: number | null;
}

/**
 * Manage-tab banner offering to combine same-frequency subscriptions into one.
 * Rendered only when the customer actually has a mergeable frequency bucket.
 */
export function MergeSubscriptionsBanner({
  onMerge,
  onDismiss,
  chips = [],
  savesShipping = false,
  savingsAmount = null,
}: MergeSubscriptionsBannerProps) {
  const hasSavingsAmount = savingsAmount != null && savingsAmount > 0;
  const title = "These are on the same schedule — combine them?";
  // Savings are a bonus under the combine question, never the pitch itself.
  // Concrete amount when we have it, qualitative fallback, nothing otherwise.
  const bonusLine = hasSavingsAmount ? (
    <>
      You&apos;d save{" "}
      <span className="tw:font-bold">${formatPrice(savingsAmount)}</span> on
      shipping.
    </>
  ) : savesShipping ? (
    "You'd save on shipping too."
  ) : null;

  return (
    <div className="tw:relative tw:flex tw:items-start tw:gap-3 tw:rounded-lg tw:border tw:border-[#523D33]/15 tw:bg-[#FDEDEE]/60 tw:p-3 tw:mb-3">
      <div className="tw:rounded-full tw:bg-white tw:p-2 tw:shrink-0">
        <Merge
          className="tw:size-4 tw:text-[#523D33]"
          strokeWidth={1.75}
        />
      </div>
      <div className="tw:flex tw:flex-col tw:flex-1 tw:min-w-0 tw:pr-4">
        <span
          className="tw:text-sm tw:font-semibold tw:leading-snug"
          style={{ color: "var(--so-text, #523D33)" }}
        >
          {title}
        </span>
        {bonusLine && (
          <span className="tw:text-xs tw:mt-1 tw:text-[#523D33]/70">
            {bonusLine}
          </span>
        )}
        {chips.length > 0 && (
          <div className="tw:flex tw:flex-wrap tw:gap-1.5 tw:mt-2">
            {chips.map((c) => (
              <span
                key={c.subscriptionId}
                className="tw:inline-flex tw:items-center tw:gap-1.5 tw:max-w-full tw:rounded-full tw:bg-white tw:border tw:border-[#523D33]/15 tw:pl-1 tw:pr-2 tw:py-1"
              >
                {c.imageUrl ? (
                  <img
                    src={c.imageUrl}
                    alt={c.title}
                    className="tw:size-5 tw:rounded-full tw:object-cover tw:shrink-0"
                    loading="lazy"
                  />
                ) : (
                  <span className="tw:size-5 tw:rounded-full tw:bg-[#523D33]/10 tw:text-[9px] tw:font-semibold tw:text-[#523D33] tw:flex tw:items-center tw:justify-center tw:shrink-0">
                    {c.title.slice(0, 1).toUpperCase()}
                  </span>
                )}
                <span className="tw:text-xs tw:font-semibold tw:text-[#523D33] tw:truncate">
                  {c.title}
                </span>
                {c.nextBillingDate && (
                  <span className="tw:text-[11px] tw:text-[#523D33]/60 tw:shrink-0">
                    {formatSubscriptionDateForDisplay(c.nextBillingDate)}
                  </span>
                )}
              </span>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={onMerge}
          className="tw:self-start tw:mt-2 tw:rounded-md tw:bg-[#523D33] tw:text-[#FAC8CA] tw:text-xs tw:font-bold tw:uppercase tw:tracking-wide tw:px-3 tw:py-1.5 tw:border-none tw:cursor-pointer tw:transition-opacity hover:tw:opacity-90"
        >
          Yes, combine them
        </button>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="tw:absolute tw:top-2 tw:right-2 tw:p-1 tw:rounded-md tw:border-none tw:bg-transparent tw:cursor-pointer tw:text-[#523D33]/60 hover:tw:text-[#523D33] hover:tw:bg-[#523D33]/5"
      >
        <X className="tw:size-4" />
      </button>
    </div>
  );
}
